import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Spinner, ButtonGroup, Button, Form, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faUsers,
    faCheckCircle,
    faChartLine,
    faUserClock,
    faTrophy,
    faSearch,
    faDatabase,
    faPlug,
    faBolt,
    faServer
} from '@fortawesome/free-solid-svg-icons';
import {
    AreaChart,
    Area,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer
} from 'recharts';
import request from '../../../../request';

/**
 * AdminStats - Premium Dashboard with real-time data and connection analytics.
 */
const AdminStats = ({ ipserv, branch_id }) => {
    const [loading, setLoading] = useState(true);
    const [summary, setSummary] = useState({
        totalStaff: 0,
        totalProfilesAssigned: 0,
        totalQueries: 0,
        activeStaff: 0
    });
    const [activity, setActivity] = useState([]);
    const [rankingPeriod, setRankingPeriod] = useState('day');
    const [ranking, setRanking] = useState([]);

    const todayStr = new Date().toISOString().split('T')[0];
    const sevenDaysAgoStr = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

    const [startDate, setStartDate] = useState(sevenDaysAgoStr);
    const [endDate, setEndDate] = useState(todayStr);
    const [connectionLoading, setConnectionLoading] = useState(false);
    const [connectionStats, setConnectionStats] = useState({
        totalQueries: 0,
        mappedConnections: 0,
        activeConnections: 0,
        avgQueries: 0
    });
    const [connectionUsage, setConnectionUsage] = useState([]);

    const fetchDashboardData = useCallback(() => {
        setLoading(true);
        request.Get(ipserv + 'dashboard-summary', { sucursalId: branch_id }, r => {
            if (r.success && r.data.length > 0) {
                setSummary(r.data[0]);
            }
        });

        request.Get(ipserv + 'dashboard-activity', { sucursalId: branch_id }, r => {
            if (r.success) {
                const formatted = (r.data || []).map(item => ({
                    name: item.day.split('-').slice(2).join('/'),
                    turnos: item.count
                }));
                setActivity(formatted);
            }
            setLoading(false);
        });
    }, [ipserv, branch_id]);

    const fetchRanking = useCallback(() => {
        request.Get(ipserv + 'dashboard-ranking', { sucursalId: branch_id, period: rankingPeriod }, r => {
            if (r.success) {
                setRanking(r.data || []);
            }
        });
    }, [ipserv, branch_id, rankingPeriod]);

    const normalizeConnectionUsage = useCallback((r) => {
        const rows = Array.isArray(r?.list) ? r.list : Array.isArray(r?.data) ? r.data : Array.isArray(r) ? r : [];
        const normalizedRows = rows.map((item, idx) => {
            const total = Number(item.totalQueries ?? item.queries ?? item.count ?? item.total ?? 0);
            const successful = Number(item.successfulQueries ?? item.success ?? item.ok ?? 0);
            const failed = Number(item.failedQueries ?? item.failed ?? item.errors ?? 0);

            return {
                id: item.ConnId ?? item.connectionId ?? idx,
                name: item.ConnName ?? item.connectionName ?? item.name ?? `Conexion ${idx + 1}`,
                driver: item.ConnDriver ?? item.driver ?? item.type ?? '-',
                database: item.ConnDB ?? item.database ?? item.schema ?? '-',
                totalQueries: total,
                successfulQueries: successful,
                failedQueries: failed,
                lastQueryAt: item.lastQueryAt ?? item.LastQueryAt ?? item.lastExecution ?? null,
                active: item.ConnActive ?? item.active ?? true
            };
        });

        const mappedConnections = Number(r?.stats?.mappedConnections ?? r?.stats?.totalConnections ?? normalizedRows.length);
        const activeConnections = Number(r?.stats?.activeConnections ?? normalizedRows.filter(c => c.active).length);
        const totalQueries = Number(r?.stats?.totalQueries ?? normalizedRows.reduce((acc, item) => acc + item.totalQueries, 0));

        return {
            rows: normalizedRows,
            stats: {
                totalQueries,
                mappedConnections,
                activeConnections,
                avgQueries: mappedConnections > 0 ? Math.round(totalQueries / mappedConnections) : 0
            }
        };
    }, []);

    const fetchConnectionUsage = useCallback(() => {
        setConnectionLoading(true);
        request.Get(ipserv + 'dashboard-connection-usage', {
            sucursalId: branch_id,
            startDate,
            endDate
        }, r => {
            if (r.success) {
                const normalized = normalizeConnectionUsage(r);
                setConnectionStats(normalized.stats);
                setConnectionUsage(normalized.rows);
            }
            setConnectionLoading(false);
        }, 'json', () => {
            setConnectionStats({ totalQueries: 0, mappedConnections: 0, activeConnections: 0, avgQueries: 0 });
            setConnectionUsage([]);
            setConnectionLoading(false);
        });
    }, [ipserv, branch_id, startDate, endDate, normalizeConnectionUsage]);

    useEffect(() => {
        if (branch_id) {
            fetchDashboardData();
            fetchConnectionUsage();
        }
    }, [branch_id, fetchDashboardData, fetchConnectionUsage]);

    useEffect(() => {
        if (branch_id) {
            fetchRanking();
        }
    }, [branch_id, fetchRanking]);

    const stats = [
        { title: 'Total Consultas', value: summary.totalQueries, icon: faChartLine, variant: 'stats-info', label: 'Consultas a modulos creados' },
        { title: 'Usuarios Staff', value: summary.totalStaff, icon: faUsers, variant: 'stats-purple', label: 'Dados de alta' },
        { title: 'Perfiles Asignados', value: summary.totalProfilesAssigned, icon: faCheckCircle, variant: 'stats-success', label: 'A usuarios staff' },
        { title: 'Staff Activo', value: summary.activeStaff, icon: faUserClock, variant: 'stats-warning', label: 'Operadores activos' }
    ];

    if (!branch_id) {
        return (
            <div className="text-center py-5 text-muted animate-in">
                <FontAwesomeIcon icon={faChartLine} size="3x" className="mb-3 opacity-20" />
                <h5>Selecciona una sucursal para ver las metricas.</h5>
            </div>
        );
    }

    return (
        <div className="animate-in pb-5">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2 style={{ fontWeight: 800, marginBottom: '0.25rem' }}>Resumen del Sistema</h2>
                    <p className="text-muted small">Panel de control y metricas operativas en tiempo real.</p>
                </div>
                {loading && <Spinner animation="border" size="sm" variant="primary" />}
            </div>

            <div className="stats-grid mb-4">
                {stats.map((stat, idx) => (
                    <div key={idx} className={`stats-card ${stat.variant}`}>
                        <div className="stats-icon-box">
                            <FontAwesomeIcon icon={stat.icon} />
                        </div>
                        <div className="stats-info">
                            <p>{stat.title}</p>
                            <h3>{stat.value}</h3>
                            <small style={{ color: '#64748b', fontSize: '0.75rem' }}>{stat.label}</small>
                        </div>
                    </div>
                ))}
            </div>

            <Row>
                <Col lg={8} className="mb-4">
                    <div className="turnero-card p-4 h-100">
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <h5 className="m-0 font-weight-bold">
                                <FontAwesomeIcon icon={faChartLine} className="mr-2 text-primary" />
                                Actividad de la Semana
                            </h5>
                            <span className="badge badge-primary-soft text-primary small">Ultimos 7 dias</span>
                        </div>

                        <div style={{ height: '320px', width: '100%' }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={activity}>
                                    <defs>
                                        <linearGradient id="colorTurns" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                                            <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border-color)" />
                                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: 'var(--text-muted)', fontSize: 12 }} dy={10} />
                                    <YAxis axisLine={false} tickLine={false} tick={{ fill: 'var(--text-muted)', fontSize: 12 }} />
                                    <Tooltip contentStyle={{ backgroundColor: '#ffffff', border: '1px solid var(--border-color)', borderRadius: '12px' }} itemStyle={{ color: 'var(--primary)' }} />
                                    <Area type="monotone" dataKey="turnos" stroke="var(--primary)" strokeWidth={3} fillOpacity={1} fill="url(#colorTurns)" />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </Col>

                <Col lg={4} className="mb-4">
                    <div className="turnero-card p-4 h-100">
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <h5 className="m-0 font-weight-bold">
                                <FontAwesomeIcon icon={faTrophy} className="mr-2 text-warning" /> Ranking Staff
                            </h5>
                            <ButtonGroup size="sm">
                                <Button variant={rankingPeriod === 'day' ? 'primary' : 'outline-secondary'} onClick={() => setRankingPeriod('day')} className="border-0" style={{ fontSize: '0.7rem' }}>Dia</Button>
                                <Button variant={rankingPeriod === 'week' ? 'primary' : 'outline-secondary'} onClick={() => setRankingPeriod('week')} className="border-0" style={{ fontSize: '0.7rem' }}>Semana</Button>
                            </ButtonGroup>
                        </div>

                        <div className="d-flex flex-column gap-3">
                            {ranking.length === 0 ? (
                                <div className="text-center py-5 text-muted small">No hay datos registrados aun.</div>
                            ) : (
                                ranking.map((user, idx) => (
                                    <div key={idx} className="p-2 rounded hover-bg-light transition-all">
                                        <div className="d-flex justify-content-between align-items-center mb-1">
                                            <div className="d-flex align-items-center gap-2">
                                                <div className="d-flex align-items-center justify-content-center rounded-circle small font-weight-bold" style={{ width: '24px', height: '24px', background: idx === 0 ? 'var(--primary)' : '#f1f5f9', color: idx === 0 ? '#fff' : 'var(--text-main)' }}>{idx + 1}</div>
                                                <span className="small font-weight-bold">{user.UsrNombre} {user.UsrApellido}</span>
                                            </div>
                                            <span className="badge bg-light text-primary">{user.totalTurns} consultas</span>
                                        </div>
                                        <div className="progress" style={{ height: '4px', background: 'var(--bg-main)' }}>
                                            <div className="progress-bar bg-primary" style={{ width: `${(user.totalTurns / (ranking[0]?.totalTurns || 1)) * 100}%` }}></div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </Col>
            </Row>

            <div className="turnero-card p-4 mt-2">
                <div className="d-flex justify-content-between align-items-end flex-wrap gap-3 mb-4 pb-3 border-bottom border-light">
                    <div>
                        <h4 style={{ fontWeight: 800, marginBottom: '0.25rem' }}>
                            <FontAwesomeIcon icon={faDatabase} className="mr-2 text-info" /> Uso de Conexiones
                        </h4>
                        <p className="text-muted small m-0">Cantidad de consultas ejecutadas sobre las conexiones mapeadas.</p>
                    </div>

                    <div className="d-flex gap-2 align-items-center">
                        <Form.Group className="mb-0">
                            <Form.Label className="tiny text-muted mb-1">Desde</Form.Label>
                            <Form.Control type="date" size="sm" value={startDate} onChange={e => setStartDate(e.target.value)} className="bg-white border-light text-main" />
                        </Form.Group>
                        <Form.Group className="mb-0">
                            <Form.Label className="tiny text-muted mb-1">Hasta</Form.Label>
                            <Form.Control type="date" size="sm" value={endDate} onChange={e => setEndDate(e.target.value)} className="bg-white border-light text-main" />
                        </Form.Group>
                        <Button variant="primary" size="sm" className="mt-4" onClick={fetchConnectionUsage} disabled={connectionLoading}>
                            <FontAwesomeIcon icon={faSearch} />
                        </Button>
                    </div>
                </div>

                {connectionLoading ? (
                    <div className="text-center py-5">
                        <Spinner animation="grow" variant="info" />
                        <p className="mt-3 text-muted small">Calculando consultas por conexion...</p>
                    </div>
                ) : (
                    <>
                        <Row className="mb-4">
                            <Col md={3} className="mb-3">
                                <div className="p-3 rounded border border-light border-opacity-10 d-flex align-items-center gap-3">
                                    <div className="text-info opacity-50"><FontAwesomeIcon icon={faBolt} size="2x" /></div>
                                    <div>
                                        <p className="text-muted tiny m-0">Consultas Periodo</p>
                                        <h5 className="m-0 font-weight-bold">{connectionStats.totalQueries} <small className="text-muted small">total</small></h5>
                                    </div>
                                </div>
                            </Col>
                            <Col md={3} className="mb-3">
                                <div className="p-3 rounded border border-light border-opacity-10 d-flex align-items-center gap-3">
                                    <div className="text-primary opacity-50"><FontAwesomeIcon icon={faPlug} size="2x" /></div>
                                    <div>
                                        <p className="text-muted tiny m-0">Mapeadas</p>
                                        <h5 className="m-0 font-weight-bold">{connectionStats.mappedConnections} <small className="text-muted small">conexiones</small></h5>
                                    </div>
                                </div>
                            </Col>
                            <Col md={3} className="mb-3">
                                <div className="p-3 rounded border border-light border-opacity-10 d-flex align-items-center gap-3">
                                    <div className="text-success opacity-50"><FontAwesomeIcon icon={faCheckCircle} size="2x" /></div>
                                    <div>
                                        <p className="text-muted tiny m-0">Activas</p>
                                        <h5 className="m-0 font-weight-bold">{connectionStats.activeConnections} <small className="text-muted small">online</small></h5>
                                    </div>
                                </div>
                            </Col>
                            <Col md={3} className="mb-3">
                                <div className="p-3 rounded border border-light border-opacity-10 d-flex align-items-center gap-3">
                                    <div className="text-warning opacity-50"><FontAwesomeIcon icon={faServer} size="2x" /></div>
                                    <div>
                                        <p className="text-muted tiny m-0">Promedio</p>
                                        <h5 className="m-0 font-weight-bold">{connectionStats.avgQueries} <small className="text-muted small">por conexion</small></h5>
                                    </div>
                                </div>
                            </Col>
                        </Row>

                        <div className="mb-4" style={{ height: '260px', width: '100%' }}>
                            {connectionUsage.length === 0 ? (
                                <div className="text-center py-5 text-muted small">No hay consultas registradas para las conexiones en este periodo.</div>
                            ) : (
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={connectionUsage} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                                        <defs>
                                            <linearGradient id="connectionUsageGradient" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="0%" stopColor="#0ea5e9" />
                                                <stop offset="100%" stopColor="#6366f1" />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border-color)" />
                                        <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: 'var(--text-muted)', fontSize: 12 }} dy={10} />
                                        <YAxis axisLine={false} tickLine={false} tick={{ fill: 'var(--text-muted)', fontSize: 12 }} />
                                        <Tooltip contentStyle={{ backgroundColor: '#ffffff', border: '1px solid var(--border-color)', borderRadius: '12px' }} itemStyle={{ color: 'var(--primary)' }} />
                                        <Bar dataKey="totalQueries" name="Consultas" fill="url(#connectionUsageGradient)" radius={[8, 8, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            )}
                        </div>

                        <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                            <Table className="table-hover m-0" style={{ fontSize: '0.85rem' }}>
                                <thead className="bg-light">
                                    <tr>
                                        <th className="border-0">Conexion</th>
                                        <th className="border-0">Motor</th>
                                        <th className="border-0">Base</th>
                                        <th className="border-0">Consultas</th>
                                        <th className="border-0">Exitosas</th>
                                        <th className="border-0">Fallidas</th>
                                        <th className="border-0">Ultima consulta</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {connectionUsage.length === 0 ? (
                                        <tr>
                                            <td colSpan="7" className="text-center py-5 text-muted">No se encontraron consultas sobre conexiones mapeadas.</td>
                                        </tr>
                                    ) : (
                                        connectionUsage.map((conn) => (
                                            <tr key={conn.id} className="transition-all">
                                                <td className="font-weight-bold text-primary">{conn.name}</td>
                                                <td><span className="badge bg-light text-dark">{String(conn.driver).toUpperCase()}</span></td>
                                                <td className="text-muted">{conn.database}</td>
                                                <td className="font-weight-bold">{conn.totalQueries}</td>
                                                <td className="text-success">{conn.successfulQueries}</td>
                                                <td className={conn.failedQueries > 0 ? 'text-danger font-weight-bold' : 'text-muted'}>{conn.failedQueries}</td>
                                                <td className="text-muted">{conn.lastQueryAt ? new Date(conn.lastQueryAt).toLocaleString() : '-'}</td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </Table>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default AdminStats;
